import { Trans } from "react-i18next";
import i18next from "src/i18n/config";

import { LicenseEvents } from "@dashboard/devices/types";
import { getEventColors } from "@dashboard/overview/components/EventChartCard/EventChartCard.utils";
import { Theme } from "@mui/material";

import { createDeviceLink, createGroupLink, createLicenseLink, extractPlainText, getActorTitle } from "./shared/eventHelpers";
import type { EventContentResult, EventContext, RendererProps } from "./shared/eventTypes";
import { renderUnknownEventContent } from "./unknown";

type LicenseCreatedEvent = Extract<LicenseEvents, { eventTypeName: "LicenseEvent.Created" }>;
const renderLicenseCreated = (props: RendererProps<LicenseCreatedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { license, group } = context;

    const eventColors = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));
    const licenseLabel = extractPlainText(license?.activationCode ?? event.data?.code ?? i18next.t("common:status.unknown"));
    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.licenseCreatedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.licenseCreatedDescription"
                values={{
                    licenseCode: licenseLabel,
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    licenseLink: createLicenseLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type LicenseDeletedEvent = Extract<LicenseEvents, { eventTypeName: "LicenseEvent.Deleted" }>;
const renderLicenseDeleted = (props: RendererProps<LicenseDeletedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { license, group } = context;

    const eventColors = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));
    const licenseLabel = extractPlainText(license?.activationCode ?? event.data?.code ?? i18next.t("common:status.unknown"));
    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.licenseDeletedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.licenseDeletedDescription"
                values={{
                    licenseCode: licenseLabel,
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    licenseLink: createLicenseLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type LicenseRevivedEvent = Extract<LicenseEvents, { eventTypeName: "LicenseEvent.Revived" }>;
const renderLicenseRevived = (props: RendererProps<LicenseRevivedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { license, group } = context;

    const eventColors = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));
    const licenseLabel = extractPlainText(license?.activationCode ?? event.data?.code ?? i18next.t("common:status.unknown"));
    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.licenseRevivedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.licenseRevivedDescription"
                values={{
                    licenseCode: licenseLabel,
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    licenseLink: createLicenseLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type LicenseRevokedEvent = Extract<LicenseEvents, { eventTypeName: "LicenseEvent.Revoked" }>;
const renderLicenseRevoked = (props: RendererProps<LicenseRevokedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { license, group } = context;

    const eventColors = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));
    const licenseLabel = extractPlainText(license?.activationCode ?? event.data?.code ?? i18next.t("common:status.unknown"));
    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.licenseRevokedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.licenseRevokedDescription"
                values={{
                    licenseCode: licenseLabel,
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                }}
                components={{
                    licenseLink: createLicenseLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type LicenseUnlinkedEvent = Extract<LicenseEvents, { eventTypeName: "LicenseEvent.Unlinked" }>;
const renderLicenseUnlinked = (props: RendererProps<LicenseUnlinkedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { license, group } = context;

    const reason = event.data?.reason;
    const eventColors = getEventColors(theme);
    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));
    const licenseLabel = extractPlainText(license?.activationCode ?? event.data?.code ?? i18next.t("common:status.unknown"));
    const groupLabel = extractPlainText(group?.name ?? event.groupId ?? i18next.t("common:status.unknown"));

    if (!reason?.length) {
        return {
            color: eventColors[event.level],
            header: i18next.t("modals:eventModals.licenseUnlinkedTitle"),
            component: (
                <Trans
                    ns="modals"
                    i18nKey="eventModals.licenseUnlinkedDescriptionWithoutReason"
                    values={{
                        licenseCode: licenseLabel,
                        groupName: groupLabel,
                        actorTitle: actorLabel,
                    }}
                    components={{
                        licenseLink: createLicenseLink(event, context),
                        groupLink: createGroupLink(event, context),
                    }}
                />
            ),
        };
    }

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.licenseUnlinkedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.licenseUnlinkedDescription"
                values={{
                    licenseCode: licenseLabel,
                    groupName: groupLabel,
                    actorTitle: actorLabel,
                    reason: reason,
                }}
                components={{
                    licenseLink: createLicenseLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type LicenseLinkedEvent = Extract<LicenseEvents, { eventTypeName: "LicenseEvent.Linked" }>;
const renderLicenseLinked = (props: RendererProps<LicenseLinkedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { device, group } = context;

    const deviceName = device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown");
    const groupName = group?.name ?? event.groupId ?? i18next.t("common:status.unknown");
    const actorTitle = getActorTitle(event);
    const eventColors = getEventColors(theme);

    const hasUserData = event.actorTypeName === "Device" && !!event.data?.user;
    const userTitle = hasUserData ? event.data?.user : actorTitle;

    if (userTitle?.length) {
        return {
            color: eventColors[event.level],
            header: i18next.t("modals:eventModals.licenseLinkedTitle"),
            component: (
                <Trans
                    ns="modals"
                    i18nKey="eventModals.licenseLinkedDescription"
                    values={{
                        deviceName,
                        groupName,
                        actorTitle: userTitle,
                    }}
                    components={{
                        deviceLink: createDeviceLink(event, context),
                        groupLink: createGroupLink(event, context),
                    }}
                />
            ),
        };
    }

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.licenseLinkedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey="eventModals.licenseLinkedDescriptionWithoutUser"
                values={{ deviceName, groupName }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

type LicenseMovedEvent = Extract<LicenseEvents, { eventTypeName: "LicenseEvent.Moved" }>;
const renderLicenseMoved = (props: RendererProps<LicenseMovedEvent>): EventContentResult | null => {
    const { event, context, theme } = props;
    const { license, device, group, allGroups } = context;

    const actorTitle = getActorTitle(event, i18next.t("common:status.unknown"));
    const eventColors = getEventColors(theme);

    const licenseCode = license?.activationCode ?? event.data?.code ?? i18next.t("common:status.unknown");
    const deviceName = device?.name ?? event.data?.deviceSerial ?? i18next.t("common:status.unknown");
    const groupName = group?.name ?? event.groupId ?? i18next.t("common:status.unknown");
    const newGroupName = allGroups?.find((g) => g.id === event.data?.group?.new)?.name ?? groupName;
    const hasDevice = !!device;

    const oldGroupId = event.data?.group?.old;
    const hasOldGroup = oldGroupId !== null && oldGroupId !== undefined;
    const oldGroupName = allGroups?.find((g) => g.id === oldGroupId)?.name ?? i18next.t("common:status.unknown");

    if (!hasDevice) {
        return {
            color: eventColors[event.level],
            header: i18next.t("modals:eventModals.licenseMovedTitle"),
            component: (
                <Trans
                    ns="modals"
                    i18nKey={
                        hasOldGroup ? "eventModals.licenseMovedDescription" : "eventModals.licenseMovedDescriptionWithoutOldGroup"
                    }
                    values={{
                        licenseCode,
                        groupName: newGroupName,
                        oldGroupName,
                        actorTitle,
                    }}
                    components={{
                        deviceLink: <span />,
                        licenseLink: createLicenseLink(event, context),
                        groupLink: createGroupLink(event, context),
                    }}
                />
            ),
        };
    }

    return {
        color: eventColors[event.level],
        header: i18next.t("modals:eventModals.licenseMovedTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey={
                    hasOldGroup
                        ? "eventModals.licenseMovedWithDeviceDescription"
                        : "eventModals.licenseMovedWithDeviceDescriptionWithoutOldGroup"
                }
                values={{
                    deviceName,
                    groupName: newGroupName,
                    oldGroupName,
                    actorTitle,
                }}
                components={{
                    deviceLink: createDeviceLink(event, context),
                    licenseLink: <span />,
                    groupLink: createGroupLink(event, context),
                }}
            />
        ),
    };
};

export const renderLicenseEventContent = (
    event: LicenseEvents,
    context: EventContext,
    theme: Theme,
): EventContentResult | null => {
    switch (event.eventTypeName) {
        case "LicenseEvent.Created":
            return renderLicenseCreated({ event, context, theme });
        case "LicenseEvent.Deleted":
            return renderLicenseDeleted({ event, context, theme });
        case "LicenseEvent.Unlinked":
            return renderLicenseUnlinked({ event, context, theme });
        case "LicenseEvent.Revived":
            return renderLicenseRevived({ event, context, theme });
        case "LicenseEvent.Revoked":
            return renderLicenseRevoked({ event, context, theme });
        case "LicenseEvent.Linked":
            return renderLicenseLinked({ event, context, theme });
        case "LicenseEvent.Moved":
            return renderLicenseMoved({ event, context, theme });
        default:
            return renderUnknownEventContent({ event, context, theme });
    }
};
